import { createUseStyles } from 'react-jss';

import { flex, touchTarget } from '@resources/mixins';
import { ThemeProps } from '@styles/theme';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    // Bounded scroll variant (scroll tables without pagination): the single
    // inner scroll area of the page.
    cardList: {
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    // Paginated variant: cards flow with the page scroll, no nested scrolling.
    cardListFlow: {
        flex: 'none',
        minHeight: 'auto',
        overflowY: 'visible',
    },
    card: {
        width: '100%',
        padding: theme.spacing(4),
        backgroundColor: theme.colors.surface_elevated,
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: theme.radius * 2,
        marginBottom: theme.spacing(2.5),
        boxShadow: theme.isDark
            ? '0 1px 4px rgba(0, 0, 0, 0.35)'
            : '0 1px 4px rgba(16, 24, 40, 0.08)',

        '&.disabled': {
            textDecoration: 'line-through',
            opacity: '0.5',
        },
    },
    cardClickable: {
        cursor: 'pointer',

        '&:hover': {
            background: theme.colors.grey_background,
        },
    },
    cardHeader: {
        ...flex('row', 'flex-start', 'flex-start'),
        gap: theme.spacing(2),
    },
    cardSelection: {
        ...flex('row', 'center', 'center'),
        ...touchTarget(),
        flexShrink: 0,
    },
    cardTitleBlock: {
        flex: 1,
        minWidth: 0,
    },
    cardTitle: {
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        fontSize: 16,
        fontWeight: 'bold',
        lineHeight: 1.4,
        overflowWrap: 'anywhere',

        '& span, & div': {
            fontSize: 16,
        },
    },
    cardSubtitle: {
        marginTop: theme.spacing(0.5),
        color: theme.colors.grey_text,
        fontFamily: theme.font.primary,
        fontSize: 13,
        lineHeight: 1.4,
        overflowWrap: 'anywhere',

        '& span, & div': {
            fontSize: 13,
        },
    },
    // Top-right corner slot (e.g. a role badge, or compact version/duration
    // metadata). Sits opposite the title block.
    cardBadge: {
        ...flex('row', 'flex-end', 'center'),
        flexShrink: 0,
        flexWrap: 'wrap',
        gap: theme.spacing(1),

        // Bare text cells (a role name) get a pill.
        '& > span': {
            display: 'inline-flex',
            alignItems: 'center',
            padding: `${theme.spacing(1)}px ${theme.spacing(2.5)}px`,
            borderRadius: 999,
            backgroundColor: theme.colors.transp_violet,
            color: theme.colors.violet_primary,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.4,
            textTransform: 'capitalize',
            whiteSpace: 'nowrap',
        },

        // Component cells (e.g. version/duration) render compact muted text.
        '& > div': {
            fontSize: 12,
            fontWeight: 600,
            color: theme.colors.grey_text,
            whiteSpace: 'nowrap',
        },
    },
    expandButton: {
        ...flex('row', 'center', 'center'),
        ...touchTarget(),
        flexShrink: 0,
        border: 'none',
        background: 'none',
        borderRadius: theme.radius,
        cursor: 'pointer',
        color: theme.colors.grey_text,

        '&:hover': {
            backgroundColor: theme.colors.icon_background,
        },
    },
    expandIcon: {
        transition: 'transform .2s ease',
    },
    expandIconOpen: {
        transform: 'scale(1, -1)',
    },
    cardChips: {
        ...flex('row', 'flex-start', 'center'),
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(2.5),

        // Bare text cells (e.g. a role name) get a consistent pill; cells that
        // render their own badges/components keep their look.
        '& > span': {
            display: 'inline-flex',
            alignItems: 'center',
            padding: `${theme.spacing(0.75)}px ${theme.spacing(2.5)}px`,
            borderRadius: 999,
            backgroundColor: theme.colors.transp_violet,
            color: theme.colors.violet_primary,
            fontSize: 13,
            lineHeight: 1.5,
            textTransform: 'capitalize',
        },
    },
    cardDetails: {
        display: 'grid',
        gridTemplateColumns: 'minmax(104px, 38%) minmax(0, 1fr)',
        columnGap: theme.spacing(3),
        rowGap: theme.spacing(2),
        alignItems: 'baseline',
        margin: `${theme.spacing(3)}px 0 0`,
    },
    cardDetailLabel: {
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
        fontSize: 12,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        lineHeight: 1.6,
    },
    cardDetailValue: {
        minWidth: 0,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        fontSize: 14,
        lineHeight: 1.6,
        overflowWrap: 'anywhere',

        // Neutralize table-oriented sizing hacks in consumer cells (fixed
        // percentage widths, oversized counters, mismatched font sizes).
        '& span, & div': {
            fontSize: 14,
            lineHeight: 1.5,
        },
        '& > div': {
            width: 'auto !important',
            maxWidth: '100%',
            height: 'auto !important',
        },
    },
    cardActions: {
        ...flex('row', 'flex-end', 'center'),
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(3),
        paddingTop: theme.spacing(2.5),
        borderTop: `1px solid ${theme.colors.grey_light}`,

        '& button': {
            ...touchTarget(),
        },
    },
    cardExpanded: {
        marginTop: theme.spacing(2),
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    skeletonLine: {
        marginBottom: theme.spacing(1.5),
    },
}));
